import React, { useContext } from "react";
import { MenuContext } from "../../widgets/Menu/context";
import { Flex } from "../Box";
import AnimatedIconComponent from "../Svg/AnimatedIconComponent";
import LiveLogos from "../MenuItems/LiveLogos";
import { FeaturedDisc, LiveItem, StyledBottomNavItem, StyledBottomNavText } from "./styles";
import { BottomNavItemProps } from "./types";

const BottomNavItem: React.FC<React.PropsWithChildren<BottomNavItemProps>> = ({
  label,
  icon,
  fillIcon,
  href,
  showItemsOnMobile = false,
  isActive = false,
  disabled = false,
  featured = false,
  live,
  ...props
}) => {
  const { linkComponent } = useContext(MenuContext);
  // while something is live its logos stand in for the icon: the icon keeps its box (hidden), so the label never moves
  const liveNow = Boolean(live && live.logos.length > 0 && live.href);
  // labels share one baseline (bottom-aligned): icons of different heights no longer shift them
  const bottomNavItemContent = (
    <Flex flexDirection="column" justifyContent="flex-end" alignItems="center" height="100%">
      {featured && icon ? (
        <FeaturedDisc $active={isActive}>{React.createElement(icon, { width: "24px", height: "24px", color: "currentColor" })}</FeaturedDisc>
      ) : icon && (
        // one fixed icon box for every tab (with or without a fill icon), so all labels sit on the same line
        <Flex height="24px" alignItems="center" justifyContent="center" position="relative" style={liveNow ? { visibility: "hidden" } : undefined}>
          <AnimatedIconComponent
            icon={icon}
            fillIcon={fillIcon}
            height="22px"
            width="21px"
            color={isActive ? "secondary" : "textSubtle"}
            isActive={isActive}
            activeBackgroundColor="backgroundAlt"
          />
        </Flex>
      )}
      <StyledBottomNavText
        data-testid="bottom-nav-label"
        color={isActive ? "text" : "textSubtle"}
        fontWeight={isActive ? "600" : "400"}
        fontSize="12px"
      >
        {label}
      </StyledBottomNavText>
    </Flex>
  );

  const item = showItemsOnMobile ? (
    <StyledBottomNavItem style={{ opacity: disabled ? 0.5 : 1 }} type="button" {...props}>
      {bottomNavItemContent}
    </StyledBottomNavItem>
  ) : (
    <StyledBottomNavItem style={{ opacity: disabled ? 0.5 : 1 }} as={linkComponent} href={href} {...props}>
      {bottomNavItemContent}
    </StyledBottomNavItem>
  );
  if (live === undefined || featured || !icon) return item;
  // the live logos are a link of their own, laid over the icon box as the tab link's sibling (never a link inside a link)
  return (
    <LiveItem>
      {item}
      <LiveLogos live={live} place="icon" />
    </LiveItem>
  );
};

export default BottomNavItem;
